import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";

import toast from "components/partials/toast/toast";

import { getPersona } from "generated/api/persona";
import { PersonaRequest } from "generated/schemas";

import { smsSimulatorPersonaKeys } from "hooks/api/query-keys";

import { SmsSimulatorPersonaAttributes } from "containers/sms-simulator/sms-simulator.types";

const personaApi = getPersona();

export const useSmsSimulatorPersonas = <TData = SmsSimulatorPersonaAttributes[]>(
  options?: Omit<
    UseQueryOptions<SmsSimulatorPersonaAttributes[], Error, TData>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: smsSimulatorPersonaKeys.list(),
    queryFn: () => personaApi.getAllPersonas(),
    ...options,
  });

export const useCreatePersona = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: PersonaRequest) => personaApi.create2(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsSimulatorPersonaKeys.lists() });
    },
    onError: () => {
      toast.error({ data: { title: "Failed to create persona" }, autoClose: false });
    },
  });
};

export const useUpdatePersona = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: PersonaRequest }) =>
      personaApi.update({ id }, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsSimulatorPersonaKeys.lists() });
    },
    onError: () => {
      toast.error({ data: { title: "Failed to update persona" }, autoClose: false });
    },
  });
};

export const useDeletePersona = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => personaApi._delete({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsSimulatorPersonaKeys.lists() });
    },
    onError: () => {
      toast.error({ data: { title: "Failed to delete persona" }, autoClose: false });
    },
  });
};
